/**
 * Tri-state LaunchDarkly flag controlling which validation source feeds the UI.
 * - off: only Agent Validator (PIF compiler) errors are displayed (default).
 * - on: both Agent Validator + Bot Compiler errors are displayed side-by-side.
 * - bot_only: only Bot Compiler errors are displayed; PIF is not invoked (kill switch).
 */
export type AgentCompilerMode = 'off' | 'on' | 'bot_only';

export const AGENT_COMPILER_FLAG_KEY = 'ps-agent-compiler-errors';

// Tolerate common LD variation aliases — case-insensitive, dash/underscore
// interchangeable. Anything unrecognised falls back to 'off'.
export const parseAgentCompilerMode = (value: unknown): AgentCompilerMode => {
  if (typeof value !== 'string') return 'off';
  const normalized = value.trim().toLowerCase().replace(/-/g, '_');
  if (normalized === 'on') return 'on';
  if (normalized === 'bot_only' || normalized === 'botonly') return 'bot_only';
  if (normalized === 'off') return 'off';
  return 'off';
};

export const readAgentCompilerMode = (
  featureFlags: unknown
): AgentCompilerMode => {
  if (typeof featureFlags !== 'object' || featureFlags === null) return 'off';
  const raw = (featureFlags as Record<string, unknown>)[
    AGENT_COMPILER_FLAG_KEY
  ];
  return parseAgentCompilerMode(raw);
};

// Bot-compiler calls must be suppressed entirely (not just hidden in the UI)
// when mode is 'off' — in that case only agent/PIF errors should be surfaced.
export const shouldRunBotCompiler = (mode: AgentCompilerMode): boolean =>
  mode === 'on' || mode === 'bot_only';
